import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSubscription } from "@/context/SubscriptionContext";

/**
 * useModuleAuth — per-module auth wall used by HR/Accounts/Store/SPD/Work-Order
 * layouts. On the **Pro** package only, the visitor must have a session with the
 * matching role (or be the master/vendor) to enter that module. Otherwise they
 * are bounced to the module's own login page so they can enter that module's
 * credentials. On **Core** this hook is a no-op — Core keeps its existing
 * single-login-via-Hub flow (admin role can access every module).
 *
 * IMPORTANT: returns a `checking` boolean. While `checking === true` the
 * consumer MUST render a loader / nothing instead of the protected content.
 * Otherwise the protected page paints for one frame before the redirect fires
 * — exactly the "Admin page flashes then bounces to login" symptom users
 * complained about. The session probe is async (importing checkSession +
 * network round-trip), so a synchronous "render nothing until decided"
 * gate is the only way to eliminate the flash.
 *
 *   const checking = useModuleAuth({ role: "hr", loginPath: "/hr-login" });
 *   if (checking) return <PageSkeleton />;
 */
export const useModuleAuth = ({ role, loginPath }) => {
  const navigate = useNavigate();
  const { package: pkg, ready } = useSubscription();
  // Start gated for EVERYONE until we know (a) the subscription package and
  // (b) the session role. Otherwise on first paint pkg is the default "core"
  // (SubscriptionContext hasn't resolved yet) and we'd flip `checking` to
  // false immediately — the protected page would render before we get the
  // chance to ask the backend who the user is. The gate is released either:
  //   • in the Core branch of the effect below (pkg confirmed === "core"), or
  //   • after the async session probe confirms an admin / master / matching role.
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Wait until SubscriptionContext has resolved so we don't bounce a Pro
    // user away while pkg is still the default "core" value during boot.
    if (!ready) return;
    if (pkg !== "pro") {
      // Core (or any non-Pro package): release the gate so the page renders.
      setChecking(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { checkSession } = await import("@/utils/auth");
        const session = await checkSession();
        if (cancelled) return;
        // Admin is a "super-role" on Pro — once an admin is logged in, every
        // module card opens directly without re-prompting for per-module
        // credentials. Same applies to master/vendor. Otherwise the visitor
        // needs the module-specific role.
        const roles = session.roles || [];
        const ok =
          session.authenticated &&
          (session.is_master || roles.includes("admin") || roles.includes(role));
        if (!ok) {
          toast.error(`Please log in to access ${role.toUpperCase()}.`);
          navigate(loginPath, { replace: true });
          return;
        }
        setChecking(false);
      } catch (err) {
        if (cancelled) return;
        console.error(`useModuleAuth (${role}) session check failed:`, err);
        navigate(loginPath, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pkg, ready, role, loginPath, navigate]);

  return checking;
};

export default useModuleAuth;
